import React, { useEffect, useState } from 'react';
import Icon from './Icon';
import { loadCostPerHeadSummary } from '../services/CostPerHeadService';
import { CostPerHeadSummary } from '../types/ui/Budget';
import '../styles/components/CostPerHeadPanel.css';

interface CostPerHeadPanelProps {
  formatCurrency: (amount: number) => string;
}

/**
 * Surfaces BOTH cost-per-head figures side by side, clearly
 * labelled, so the falling fully-loaded average never gets mistaken for
 * the marginal (add-guest-decision) number:
 *   - Fully-loaded average = grand total / confirmed guests. Drops as
 *     headcount grows — a display of budget spread, not of what one more
 *     guest costs.
 *   - Marginal (variable-only) rate, per event. A guest's real marginal
 *     cost is the SUM of the per-event rates for every event they attend,
 *     so this is a breakdown rather than one blended flat number.
 *
 * Ported from Kalligo (I-Alpha's parity app) — see kalligo-dashboard's
 * CostPerHeadPanel.tsx.
 */
const CostPerHeadPanel: React.FC<CostPerHeadPanelProps> = ({ formatCurrency }) => {
  const [summary, setSummary] = useState<CostPerHeadSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    loadCostPerHeadSummary().then((data) => {
      if (!cancelled) {
        setSummary(data);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="cph-panel cph-panel--loading" aria-busy="true">
        <div className="cph-skeleton" />
      </section>
    );
  }

  // Until there are confirmed guests there is nothing to average: show nothing, the panel appears when RSVPs come in.
  if (!summary || summary.totalConfirmedGuests === 0) return null;

  // A guest attending every event costs the sum of every event's marginal
  // rate — surfaced as a reference figure, but the per-event table below is
  // the number to actually use once a guest doesn't attend all events.
  const marginalIfAllEvents = summary.events.reduce((sum, e) => sum + e.perHeadRate, 0);

  return (
    <section className="cph-panel">
      <button
        type="button"
        className="cph-header cph-header--toggle"
        onClick={() => setCollapsed(c => !c)}
        aria-expanded={!collapsed}
      >
        <h3>Cost per head</h3>
        <span className="cph-guest-count">{summary.totalConfirmedGuests.toLocaleString()} confirmed guests</span>
        <Icon name={collapsed ? 'chevronDown' : 'chevronUp'} size={16} />
      </button>

      {!collapsed && (
      <>
      <div className="cph-figures">
        <div className="cph-figure cph-figure--average">
          <div className="cph-figure-label">
            Fully-loaded average
            <span className="cph-figure-sublabel">total budget ÷ confirmed guests</span>
          </div>
          <div className="cph-figure-value">{formatCurrency(summary.averageCostPerHead)}</div>
          <div className="cph-figure-note">Drops as headcount grows — not the number for an add-guest decision.</div>
        </div>

        <div className="cph-figure cph-figure--marginal">
          <div className="cph-figure-label">
            Marginal, all events
            <span className="cph-figure-sublabel">variable cost only, if a guest attends every event</span>
          </div>
          <div className="cph-figure-value">{formatCurrency(marginalIfAllEvents)}</div>
          <div className="cph-figure-note">Flat per guest — use this (or the per-event rate below) to price one more guest.</div>
        </div>
      </div>

      <div className="cph-band" data-testid="cph-cost-band">
        <div className="cph-breakdown-title">Cost band</div>
        <p className="cph-breakdown-hint">
          Narrows as RSVPs land — floor is confirmed-only, ceiling assumes everyone invited plus realistic
          crashers. {Math.round(summary.costBand.rsvpConfidencePct)}% of invited guests have responded so far.
        </p>
        <div className="cph-band-track">
          <div className="cph-band-point cph-band-point--floor">
            <span className="cph-band-point-label">Floor</span>
            <span className="cph-band-point-value">{formatCurrency(summary.costBand.floorCost)}</span>
            <span className="cph-band-point-sub">{summary.costBand.floorHeadcount.toLocaleString()} confirmed</span>
          </div>
          <div className="cph-band-point cph-band-point--expected">
            <span className="cph-band-point-label">Expected</span>
            <span className="cph-band-point-value">{formatCurrency(summary.costBand.expectedCost)}</span>
            <span className="cph-band-point-sub">{summary.costBand.expectedHeadcount.toLocaleString()} heads</span>
          </div>
          <div className="cph-band-point cph-band-point--ceiling">
            <span className="cph-band-point-label">Ceiling</span>
            <span className="cph-band-point-value">{formatCurrency(summary.costBand.ceilingCost)}</span>
            <span className="cph-band-point-sub">{summary.costBand.ceilingHeadcount.toLocaleString()} heads</span>
          </div>
        </div>
      </div>

      {summary.events.length > 0 && (
        <div className="cph-breakdown">
          <div className="cph-breakdown-title">Marginal per event</div>
          <p className="cph-breakdown-hint">A guest's true marginal cost is the sum of the rows for the events they'll actually attend.</p>
          <table className="cph-table">
            <thead>
              <tr>
                <th scope="col">Event</th>
                <th scope="col">Confirmed</th>
                <th scope="col">Marginal / head</th>
                <th scope="col">Cost band (floor–ceiling)</th>
              </tr>
            </thead>
            <tbody>
              {summary.events.map((event) => (
                <tr key={event.eventId}>
                  <td>{event.eventTitle}</td>
                  <td>{event.headcountConfirmed.toLocaleString()}</td>
                  <td>{formatCurrency(event.marginalCostPerHead)}</td>
                  <td>
                    {formatCurrency(event.costBand.floorCost)} – {formatCurrency(event.costBand.ceilingCost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary.excludedItemIds.length > 0 && (
        <p className="cph-excluded-note">
          {summary.excludedItemIds.length} budget item{summary.excludedItemIds.length === 1 ? '' : 's'} missing a rate or event link {summary.excludedItemIds.length === 1 ? 'was' : 'were'} left out of these figures.
        </p>
      )}
      </>
      )}
    </section>
  );
};

export default CostPerHeadPanel;
